<div class="testimony-edit">
  <div class="testimony-edit__header">
    <div>
      <h1 class="page-title">Testimony Report</h1>
      <p class="page-subtitle">
        {{ report?.requestNo || report?.requestId || "Loading..." }} · {{ report?.teamLeader?.name || "-" }} · {{
        report?.partner?.name || "-" }}
      </p>
    </div>
    <div class="testimony-edit__actions">
      <app-button type="button" variant="secondary" (click)="goBack()">Back</app-button>
      <app-button type="button" variant="secondary" [disabled]="isSaving || isSubmitted" (click)="save()">
        {{ isSaving ? "Saving..." : "Save Report" }}
      </app-button>
      <app-button type="button" [disabled]="isSaving || isSubmitting || isSubmitted" (click)="submit()">
        {{ isSubmitting ? "Submitting..." : "Submit Report" }}
      </app-button>
    </div>
  </div>

  @if (report?.dcmSync?.error) {
  <div class="testimony-edit__alert">{{ report?.dcmSync?.error }}</div>
  }

  <form [formGroup]="form" class="testimony-edit__workbench">
    <div class="testimony-edit__main">
      <app-card>
        <h2>Report Details</h2>
        <div class="testimony-edit__form-grid testimony-edit__form-grid--details">
          <label>
            Progress Status
            <div class="testimony-edit__status-row">
              <div class="testimony-edit__select-wrap">
                <select formControlName="status" [disabled]="isSubmitted">
                  @if (isSubmitted) {
                  <option value="Submitted">Submitted</option>
                  } @else if (report?.status === 'SyncFailed') {
                  <option value="SyncFailed">Sync failed</option>
                  } @else { @for (option of progressStatusOptions; track option.value) {
                  <option [value]="option.value">{{ option.label }}</option>
                  } }
                </select>
                <ng-glyph class="testimony-edit__select-caret" name="expand_more" size="18"></ng-glyph>
              </div>
              <button
                type="button"
                class="testimony-edit__status-update-button"
                [disabled]="isSaving || isSubmitted || !canUpdateProgressStatus"
                (click)="updateStatus()"
                aria-label="Update progress status"
                title="Update status">
                <ng-glyph name="save" size="20"></ng-glyph>
              </button>
            </div>
          </label>
          <label>
            Report Date
            <input type="date" formControlName="reportDate" [readonly]="isSubmitted" />
          </label>
        </div>
        @if (report?.status === 'Submitted' || report?.dcmSync?.recordId || report?.dcmSync?.lastSyncedAt) {
        <div class="testimony-edit__sync-panel">
          <h3>Submission Sync</h3>
          <p>
            Record {{ report?.dcmSync?.recordId || "-" }} · Last synced {{ formatDate(report?.dcmSync?.lastSyncedAt || undefined) }}
          </p>
        </div>
        }
      </app-card>

      <app-card>
        <div class="testimony-edit__section-header">
          <h2>Material Usage</h2>
          <div class="testimony-edit__totals" aria-label="Material totals">
            <span>Requested {{ getMaterialTotal("requestedQty") }}</span>
            <span>Used {{ getMaterialTotal("usedQty") }}</span>
            <span>Left {{ getMaterialTotal("leftQty") }}</span>
          </div>
        </div>
        <div formArrayName="materials" class="testimony-edit__material-table">
          <div class="testimony-edit__material-head" aria-hidden="true">
            <span>Material</span>
            <span>Requested</span>
            <span>Used</span>
            <span>Left</span>
          </div>
          <div class="testimony-edit__material-list">
            @for (control of materials.controls; track $index) {
            <div
              [formGroupName]="$index"
              class="testimony-edit__material-row"
              [class.testimony-edit__material-row--invalid]="control.hasError('materialUsageExceeded') && (control.dirty || control.touched)">
              <div class="testimony-edit__material-info">
                <strong>{{ control.get("materialLabel")?.value }}</strong>
                <span>{{ control.get("sku")?.value }} · Series ID {{ control.get("dcmSeriesId")?.value }}</span>
              </div>
              <label>
                <span>Requested</span>
                <input type="number" min="0" formControlName="requestedQty" readonly />
              </label>
              <label>
                <span>Used</span>
                <input
                  type="number"
                  min="0"
                  appQtyInput
                  [attr.max]="control.get('requestedQty')?.value ?? null"
                  formControlName="usedQty"
                  [readonly]="isSubmitted" />
              </label>
              <label>
                <span>Left</span>
                <input type="number" min="0" formControlName="leftQty" readonly />
              </label>
              @if (control.hasError('materialUsageExceeded') && (control.dirty || control.touched)) {
              <p class="testimony-edit__material-error">Used plus left cannot exceed requested.</p>
              }
            </div>
            }
          </div>
        </div>
      </app-card>

      <app-card>
        <div class="testimony-edit__section-header">
          <h2>Stories & Media</h2>
          <app-button type="button" variant="secondary" [disabled]="isSubmitted" (click)="addTestimony()">
            <ng-glyph name="add" size="18"></ng-glyph>
            Add Story
          </app-button>
        </div>
        <div formArrayName="testimonies" class="testimony-edit__stories">
          @for (control of testimonies.controls; track $index) {
          <div [formGroupName]="$index" class="testimony-edit__story">
            <div class="testimony-edit__story-header">
              <h3>Story {{ $index + 1 }}</h3>
              <app-button
                type="button"
                variant="danger"
                [disabled]="isSubmitted || testimonies.length <= 1"
                (click)="removeTestimony($index)">
                <ng-glyph name="delete" size="18"></ng-glyph>
              </app-button>
            </div>
            <div class="testimony-edit__story-body">
              <label class="testimony-edit__story-date">
                Testimony Date
                <input type="date" formControlName="testimonyDate" [readonly]="isSubmitted" />
              </label>
              <div class="testimony-edit__field">
                <span>Testimony</span>
                <app-rich-text-editor
                  formControlName="text"
                  placeholder="Write testimony..."
                  [disabled]="isSubmitted"></app-rich-text-editor>
              </div>
              <app-file-uploader-base
                [multiple]="true"
                accept="image/*,video/*"
                viewMode="mixed"
                label="Upload images or videos"
                hint="Images can be included as report text links; videos stay in admin as public media links."
                [disabled]="isSubmitted"
                [items]="getFileItems($index)"
                [errorMessage]="uploadErrors[$index] || null"
                (filesSelected)="uploadFiles($index, $event)"
                (removeRequested)="removeFile($index, $event)"></app-file-uploader-base>
            </div>
          </div>
          }
        </div>
      </app-card>
    </div>

    <aside class="testimony-edit__aside">
      <app-card>
        <h2>Impact</h2>
        <div class="testimony-edit__form-grid testimony-edit__form-grid--impact">
          <label>
            <span>How many students enrolled in a new course this year?</span>
            <span class="testimony-edit__field-hint">Count each student once, even if they took more than one course.</span>
            <input type="number" min="0" appQtyInput formControlName="studentsEnrolled" [readonly]="isSubmitted" />
          </label>
          <label>
            <span>Of those students, how many enrolled in a second new course the same year?</span>
            <span class="testimony-edit__field-hint">Example: a student takes Explorers 1, then later takes Explorers 2.</span>
            <input type="number" min="0" appQtyInput formControlName="studentsEnrolledMultiple" [readonly]="isSubmitted" />
          </label>
          <label>
            <span>How many students completed their course in your Mailbox Club program?</span>
            <input type="number" min="0" appQtyInput formControlName="studentsCompleted" [readonly]="isSubmitted" />
          </label>
          <label>
            <span>How many students indicated that they were saved through the lessons this year?</span>
            <input type="number" min="0" appQtyInput formControlName="studentsSaved" [readonly]="isSubmitted" />
          </label>
          <label>
            <span>How many schools/churches/ministries participated in your Mailbox Club program this year?</span>
            <input type="number" min="0" appQtyInput formControlName="ministryParticipate" [readonly]="isSubmitted" />
          </label>
          <label>
            <span>How many Volunteers/Club Leaders in total assisted you in your Mailbox Club program this year?</span>
            <span class="testimony-edit__field-hint">Total including those under 18.</span>
            <input type="number" min="0" appQtyInput formControlName="volunteerCount" [readonly]="isSubmitted" />
          </label>
          <label>
            <span>How many of these Volunteers/Club Leaders are 18 years and younger?</span>
            <span class="testimony-edit__field-hint">Subset of total.</span>
            <input type="number" min="0" appQtyInput formControlName="volunteerChild" [readonly]="isSubmitted" />
          </label>
        </div>
      </app-card>
    </aside>
  </form>
</div>
